import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export type NoiseMode = "add-noise" | "median" | "dust-scratches" | "reduce-noise";

export interface NoiseParams {
  mode: NoiseMode;
  amount: number;
  distribution: "uniform" | "gaussian";
  monochrome: boolean;
  radius: number;
  threshold: number;
  strength: number;
  preserveDetails: boolean;
}

function clamp8(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : Math.round(v);
}

function gaussianRandom(): number {
  let u = 0;
  let v = 0;
  while (u === 0) u = Math.random();
  while (v === 0) v = Math.random();
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}

function luminance(r: number, g: number, b: number): number {
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

function medianValue(values: number[]): number {
  const sorted = values.slice().sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length / 2)]!;
}

function collectWindow(
  src: Uint8ClampedArray,
  w: number,
  h: number,
  cx: number,
  cy: number,
  radius: number,
): { rs: number[]; gs: number[]; bs: number[] } {
  const rs: number[] = [];
  const gs: number[] = [];
  const bs: number[] = [];
  const r2 = radius;
  for (let dy = -r2; dy <= r2; dy++) {
    let sy = cy + dy;
    if (sy < 0) sy = 0;
    else if (sy >= h) sy = h - 1;
    for (let dx = -r2; dx <= r2; dx++) {
      let sx = cx + dx;
      if (sx < 0) sx = 0;
      else if (sx >= w) sx = w - 1;
      const o = (sy * w + sx) * 4;
      rs.push(src[o]!);
      gs.push(src[o + 1]!);
      bs.push(src[o + 2]!);
    }
  }
  return { rs, gs, bs };
}

function applyAddNoise(
  src: Uint8ClampedArray,
  dst: Uint8ClampedArray,
  w: number,
  h: number,
  amount: number,
  distribution: "uniform" | "gaussian",
  monochrome: boolean,
): void {
  const n = w * h;
  const stddev = amount / 3;
  for (let i = 0; i < n; i++) {
    const o = i * 4;
    let noise: number;
    if (distribution === "uniform") {
      noise = (Math.random() * 2 - 1) * amount;
    } else {
      noise = gaussianRandom() * stddev;
    }
    if (monochrome) {
      const lum = luminance(src[o]!, src[o + 1]!, src[o + 2]!);
      const adj = clamp8(lum + noise);
      const diff = adj - lum;
      dst[o] = clamp8(src[o]! + diff);
      dst[o + 1] = clamp8(src[o + 1]! + diff);
      dst[o + 2] = clamp8(src[o + 2]! + diff);
    } else {
      dst[o] = clamp8(src[o]! + (distribution === "uniform" ? (Math.random() * 2 - 1) * amount : gaussianRandom() * stddev));
      dst[o + 1] = clamp8(src[o + 1]! + (distribution === "uniform" ? (Math.random() * 2 - 1) * amount : gaussianRandom() * stddev));
      dst[o + 2] = clamp8(src[o + 2]! + (distribution === "uniform" ? (Math.random() * 2 - 1) * amount : gaussianRandom() * stddev));
    }
    dst[o + 3] = src[o + 3]!;
  }
}

function applyMedian(
  src: Uint8ClampedArray,
  dst: Uint8ClampedArray,
  w: number,
  h: number,
  radius: number,
): void {
  const n = w * h;
  for (let i = 0; i < n; i++) {
    const y = Math.floor(i / w);
    const x = i % w;
    const o = i * 4;
    const { rs, gs, bs } = collectWindow(src, w, h, x, y, radius);
    dst[o] = medianValue(rs);
    dst[o + 1] = medianValue(gs);
    dst[o + 2] = medianValue(bs);
    dst[o + 3] = src[o + 3]!;
  }
}

function applyDustScratches(
  src: Uint8ClampedArray,
  dst: Uint8ClampedArray,
  w: number,
  h: number,
  radius: number,
  threshold: number,
): void {
  const n = w * h;
  for (let i = 0; i < n; i++) {
    const y = Math.floor(i / w);
    const x = i % w;
    const o = i * 4;
    const { rs, gs, bs } = collectWindow(src, w, h, x, y, radius);
    const mr = medianValue(rs);
    const mg = medianValue(gs);
    const mb = medianValue(bs);
    if (Math.abs(src[o]! - mr) > threshold) {
      dst[o] = mr;
    } else {
      dst[o] = src[o]!;
    }
    if (Math.abs(src[o + 1]! - mg) > threshold) {
      dst[o + 1] = mg;
    } else {
      dst[o + 1] = src[o + 1]!;
    }
    if (Math.abs(src[o + 2]! - mb) > threshold) {
      dst[o + 2] = mb;
    } else {
      dst[o + 2] = src[o + 2]!;
    }
    dst[o + 3] = src[o + 3]!;
  }
}

function edgeDetect(
  src: Uint8ClampedArray,
  w: number,
  h: number,
  threshold: number,
): Uint8Array {
  const edges = new Uint8Array(w * h);
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const o = (y * w + x) * 4;
      const lo = ((y - 1) * w + x) * 4;
      const ro = ((y + 1) * w + x) * 4;
      const l0 = luminance(src[o]!, src[o + 1]!, src[o + 2]!);
      const l1 = luminance(src[lo]!, src[lo + 1]!, src[lo + 2]!);
      const l2 = luminance(src[ro]!, src[ro + 1]!, src[ro + 2]!);
      const grad = Math.abs(l0 - l1) + Math.abs(l0 - l2);
      if (grad > threshold) {
        edges[y * w + x] = 1;
      }
    }
  }
  return edges;
}

function applyReduceNoise(
  src: Uint8ClampedArray,
  dst: Uint8ClampedArray,
  w: number,
  h: number,
  strength: number,
): void {
  const edgeThreshold = Math.max(1, 40 - strength * 0.35);
  const edges = edgeDetect(src, w, h, edgeThreshold);
  const n = w * h;
  for (let i = 0; i < n; i++) {
    const y = Math.floor(i / w);
    const x = i % w;
    const o = i * 4;
    if (edges[i] === 1) {
      dst[o] = src[o]!;
      dst[o + 1] = src[o + 1]!;
      dst[o + 2] = src[o + 2]!;
      dst[o + 3] = src[o + 3]!;
    } else {
      const { rs, gs, bs } = collectWindow(src, w, h, x, y, 1);
      dst[o] = medianValue(rs);
      dst[o + 1] = medianValue(gs);
      dst[o + 2] = medianValue(bs);
      dst[o + 3] = src[o + 3]!;
    }
  }
}

export const NoiseSuite: Filter<NoiseParams> = {
  id: "noise-suite",
  displayName: "Noise",
  defaultParams: {
    mode: "add-noise",
    amount: 10,
    distribution: "uniform",
    monochrome: false,
    radius: 1,
    threshold: 0,
    strength: 10,
    preserveDetails: true,
  },

  apply(
    input: PixelBuffer,
    params: NoiseParams,
    _selection: Selection | null,
  ): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;

    if (w === 0 || h === 0) {
      dst.set(src);
      return out;
    }

    switch (params.mode) {
      case "add-noise":
        applyAddNoise(src, dst, w, h, params.amount, params.distribution, params.monochrome);
        break;
      case "median":
        applyMedian(src, dst, w, h, params.radius);
        break;
      case "dust-scratches":
        applyDustScratches(src, dst, w, h, params.radius, params.threshold);
        break;
      case "reduce-noise":
        applyReduceNoise(src, dst, w, h, params.strength);
        break;
    }
    return out;
  },
};
